import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Badge, Banner, Button, Card, PageHeader, Spinner } from "@magarac/ui";
import {
  jobDetailSchema,
  jobResponseSchema,
  visitResponseSchema,
  type JobDetail,
  type VisitStatus,
} from "@magarac/shared/schemas/job";
import { todayIn } from "@magarac/shared/time/tz";
import { BookVisitDialog } from "../../../components/BookVisitDialog";
import { HelpIcon } from "../../../components/HelpIcon";
import { NotesPanel } from "../../../components/NotesPanel";
import { EditJobDialog } from "../../../components/EditDialogs";
import { DocumentsCard } from "../../../components/DocumentsCard";
import { PaymentCard } from "../../../components/PaymentCard";
import { PhotosPanel } from "../../../components/PhotosPanel";
import { api } from "../../../lib/api";
import { messageFor } from "../../../lib/errors";
import {
  addressLine,
  directionsUrl,
  formatDay,
  formatLocalDate,
  formatMoney,
  formatPhone,
  formatWindow,
  JOB_STATUS_LABEL,
  telUrl,
  VISIT_STATUS_LABEL,
} from "../../../lib/format";
import { useSetting } from "../../../lib/settings";
import { useIsOffice, useTenant } from "../../../lib/tenant";
import { customerDetailSchema } from "@magarac/shared/schemas/customer";

export const Route = createFileRoute("/_app/jobs/$id")({
  component: JobPage,
});

const NEXT: Partial<Record<VisitStatus, Array<{ status: VisitStatus; label: string }>>> = {
  scheduled: [{ status: "en_route", label: "On my way" }],
  confirmed: [{ status: "en_route", label: "On my way" }],
  en_route: [
    { status: "in_progress", label: "Start work" },
    { status: "no_show", label: "Nobody home" },
  ],
  in_progress: [{ status: "complete", label: "Finish" }],
};

/** CRM-11, CRM-12, CRM-13, CRM-16, SCH-10, SCH-11. Screen key: jobs.detail */
function JobPage() {
  const { id } = Route.useParams();
  const { active } = useTenant();
  const office = useIsOffice();
  const [editing, setEditing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const queryClient = useQueryClient();
  const scheduling = useSetting("scheduling");
  const tz = scheduling.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const key = ["jobs", active?.id, id];
  const detail = useQuery({
    queryKey: key,
    queryFn: () => api.get(`/jobs/${id}`, jobDetailSchema),
    enabled: Boolean(active),
  });
  const customerProps = useQuery({
    queryKey: ["customers", active?.id, detail.data?.customer.id],
    queryFn: () => api.get(`/customers/${detail.data!.customer.id}`, customerDetailSchema),
    enabled: Boolean(active && detail.data && office),
  });
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["jobs"] });
    await queryClient.invalidateQueries({ queryKey: ["calendar"] });
  };
  // Ticks show at once on a slow phone connection; a failed save puts the box back.
  const toggle = useMutation({
    mutationFn: (v: { index: number; done: boolean }) =>
      api.post(`/jobs/${id}/checklist`, v, jobResponseSchema),
    onMutate: async (v) => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<JobDetail>(key);
      if (before) {
        queryClient.setQueryData<JobDetail>(key, {
          ...before,
          job: {
            ...before.job,
            checklist: before.job.checklist.map((item, i) =>
              i === v.index ? { ...item, done: v.done } : item,
            ),
          },
        });
      }
      return { before };
    },
    onError: (err, _v, context) => {
      if (context?.before) queryClient.setQueryData(key, context.before);
      setError(messageFor(err));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const setStatus = useMutation({
    mutationFn: (status: "complete" | "canceled" | "reopen") =>
      api.post(`/jobs/${id}/status`, { status }, jobResponseSchema),
    onSuccess: refresh,
    onError: (err) => setError(messageFor(err)),
  });
  const visitStatus = useMutation({
    mutationFn: (v: { visitId: string; status: VisitStatus; from: VisitStatus }) =>
      api.post(
        `/visits/${v.visitId}/status`,
        { status: v.status, from: v.from },
        visitResponseSchema,
      ),
    onSuccess: refresh,
    onError: (err) => {
      setError(messageFor(err));
      void refresh();
    },
  });
  const cancelVisit = useMutation({
    mutationFn: (visitId: string) => api.post(`/visits/${visitId}/cancel`, {}, visitResponseSchema),
    onSuccess: refresh,
    onError: (err) => setError(messageFor(err)),
  });

  // Disabled until the business is known, so "not loading" does not mean "loaded".
  if (detail.error) return <Banner tone="danger">{messageFor(detail.error)}</Banner>;
  if (!detail.data) return <Spinner />;
  const d = detail.data;
  const j = d.job;
  const closed = j.status === "complete" || j.status === "canceled";
  const openVisits = d.visits.filter((v) =>
    ["scheduled", "confirmed", "en_route", "in_progress"].includes(v.status),
  );

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={`Job #${j.number}: ${d.jobTypeName}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={JOB_STATUS_LABEL[j.status].tone}>{JOB_STATUS_LABEL[j.status].label}</Badge>
            {j.urgency === "urgent" ? <Badge tone="danger">Urgent</Badge> : null}
            {j.dueOn ? <span>Due {formatLocalDate(j.dueOn)}</span> : null}
          </span>
        }
        help={<HelpIcon screen="jobs.detail" />}
        actions={
          office && !closed ? (
            <Button onClick={() => setBooking(true)}>
              {openVisits.length ? "Book another visit" : "Book a visit"}
            </Button>
          ) : null
        }
      />
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Customer">
          <Link
            to="/customers/$id"
            params={{ id: d.customer.id }}
            className="text-lg font-medium text-brand-600 underline"
          >
            {d.customer.name}
          </Link>
          <div className="mt-2 flex flex-wrap gap-2">
            {d.customer.primaryPhone ? (
              <a
                href={telUrl(d.customer.primaryPhone)}
                className="inline-flex min-h-11 items-center rounded-lg border border-line px-4"
              >
                Call {formatPhone(d.customer.primaryPhone)}
              </a>
            ) : null}
            {d.property ? (
              <a
                href={directionsUrl(d.property.address)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex min-h-11 items-center rounded-lg border border-line px-4"
              >
                Directions
              </a>
            ) : null}
          </div>
          {d.property ? (
            <div className="mt-3 text-base">
              <div>{addressLine(d.property.address)}</div>
              <div className="text-sm text-muted">
                {[
                  d.property.gateCode ? `Gate code ${d.property.gateCode}` : null,
                  d.property.accessNotes,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          ) : null}
          {j.description ? <p className="mt-3 whitespace-pre-wrap">{j.description}</p> : null}
          {j.customerNotes ? (
            <p className="mt-2 text-sm">From the customer: {j.customerNotes}</p>
          ) : null}
          {office && j.internalNotes ? (
            <p className="mt-2 text-sm text-muted">{j.internalNotes}</p>
          ) : null}
          {j.depositRequiredCents > 0 ? (
            <p className="mt-2 text-sm">
              Deposit {formatMoney(j.depositRequiredCents)} · paid {formatMoney(j.depositPaidCents)}
            </p>
          ) : null}
        </Card>

        <Card title="Visits">
          {d.visits.length === 0 ? <p className="text-muted">Not on the schedule yet.</p> : null}
          <ul className="space-y-3">
            {d.visits.map((v) => (
              <li key={v.id} className="rounded-lg bg-canvas p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">
                    {formatDay(v.windowStart, tz)}, {formatWindow(v, tz)}
                  </span>
                  <Badge tone={VISIT_STATUS_LABEL[v.status].tone}>
                    {VISIT_STATUS_LABEL[v.status].label}
                  </Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(NEXT[v.status] ?? []).map((s) => (
                    <Button
                      key={s.status}
                      variant={s.status === "no_show" ? "secondary" : "primary"}
                      busy={
                        visitStatus.isPending &&
                        visitStatus.variables?.visitId === v.id &&
                        visitStatus.variables.status === s.status
                      }
                      onClick={() =>
                        visitStatus.mutate({ visitId: v.id, status: s.status, from: v.status })
                      }
                    >
                      {s.label}
                    </Button>
                  ))}
                  {office && ["scheduled", "confirmed"].includes(v.status) ? (
                    <>
                      <Link
                        to="/calendar"
                        search={{
                          day: new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(
                            new Date(v.plannedStart),
                          ),
                        }}
                        className="inline-flex min-h-11 items-center rounded-lg border border-line px-4"
                      >
                        Move on calendar
                      </Link>
                      <Button
                        variant="ghost"
                        onClick={() => cancelVisit.mutate(v.id)}
                        busy={cancelVisit.isPending && cancelVisit.variables === v.id}
                      >
                        Cancel visit
                      </Button>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Card>

        {j.checklist.length > 0 ? (
          <Card title="Checklist">
            <ul className="space-y-1">
              {j.checklist.map((item, index) => (
                <li key={`${index}-${item.label}`}>
                  <label className="flex min-h-11 items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-5 w-5"
                      checked={item.done}
                      onChange={(e) => toggle.mutate({ index, done: e.target.checked })}
                    />
                    <span className={item.done ? "text-muted line-through" : ""}>{item.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        {d.assets.length > 0 ? (
          <Card title="Equipment on this job">
            <ul className="space-y-1">
              {d.assets.map((a) => (
                <li key={a.id}>
                  <span className="font-medium">{a.type}</span>
                  {a.make || a.model ? `: ${[a.make, a.model].filter(Boolean).join(" ")}` : ""}
                  {a.serial ? (
                    <span className="text-sm text-muted"> · serial {a.serial}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        {office ? (
          <EditJobDialog
            key={`${d.job.id}-${d.job.updatedAt}`}
            open={editing}
            onClose={() => setEditing(false)}
            job={d.job}
            properties={(customerProps.data?.properties ?? []).map((p) => ({
              id: p.id,
              label: `${p.label ? `${p.label}: ` : ""}${addressLine(p.address)}`,
            }))}
          />
        ) : null}
        <PaymentCard key={`${d.job.id}-${d.job.amountDueCents}`} job={d.job} />
        {office ? <DocumentsCard jobId={id} closed={d.job.status === "canceled"} /> : null}
        <PhotosPanel jobId={id} photos={d.photos} />
        <NotesPanel parentType="job" parentId={id} />
      </div>

      {office ? (
        <div className="mt-6 flex flex-wrap gap-2">
          {!closed ? (
            <>
              <Button
                variant="secondary"
                onClick={() => setStatus.mutate("complete")}
                busy={setStatus.isPending && setStatus.variables === "complete"}
              >
                Mark job done
              </Button>
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Edit job
              </Button>
              {confirmCancel ? (
                <div className="w-full rounded-lg border border-danger p-3">
                  <p className="mb-2">
                    Cancel job #{d.job.number}?
                    {openVisits.length > 0
                      ? ` ${openVisits.length} booked visit${openVisits.length === 1 ? "" : "s"} will be canceled and the customer told.`
                      : ""}{" "}
                    Reopening the job later does not bring the visits back.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="danger"
                      onClick={() => setStatus.mutate("canceled")}
                      busy={setStatus.isPending && setStatus.variables === "canceled"}
                    >
                      Yes, cancel the job
                    </Button>
                    <Button variant="secondary" onClick={() => setConfirmCancel(false)}>
                      Keep it
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="danger" onClick={() => setConfirmCancel(true)}>
                  Cancel job
                </Button>
              )}
            </>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setStatus.mutate("reopen")}
              busy={setStatus.isPending}
            >
              Reopen job
            </Button>
          )}
        </div>
      ) : null}

      {office ? (
        <BookVisitDialog
          open={booking}
          onClose={() => setBooking(false)}
          job={{ id: j.id, jobTypeId: j.jobTypeId, propertyId: j.propertyId }}
          timezone={tz}
          today={todayIn(tz)}
        />
      ) : null}
    </div>
  );
}
